import React, {
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import PropTypes from "prop-types";
import { ContextMenu } from "primereact/contextmenu";
import { clampMenuToViewport } from "./chartContextMenuEvent.js";
// Lazy load heavy modal
const IndicatorEditModal = lazy(
  () => import("../../Modals/IndicatorEditModal"),
);
import indicatorRegistry from "../../../Registry/Indicators/IndicatorRegistry";
import { useChartsStore } from "../../../store/chartStore";
import getChartHostBody from "~/utils/chartHostBody";

const IndicatorContextMenu = forwardRef(
  ({ chartId, onEditSave, onRemove }, ref) => {
    const ctxRef = useRef(null);
    const [selectedIndicator, setSelectedIndicator] = useState(null);
    const [editVisible, setEditVisible] = useState(false);
    // Janela onde o gráfico alvo vive — resolvida no clique, nunca no render (#375).
    const [editHostBody, setEditHostBody] = useState(null);
    // PoC #popout: show deferred one commit so the ContextMenu portal can
    // re-target appendTo to the clicked window's body before positioning.
    const [showRequest, setShowRequest] = useState(null);
    const showSeqRef = useRef(0);

    React.useEffect(() => {
      if (!showRequest) return;
      ctxRef.current?.show(showRequest.showEvent);
    }, [showRequest]);

    // Get chart indicators for series-as-source support (narrow selector avoids re-render on every tick)
    const chartIndicators =
      useChartsStore((state) => state.getChart(chartId)?.indicators) || [];

    // Get the registry entry for the selected indicator
    const registryEntry = useMemo(() => {
      if (!selectedIndicator || !selectedIndicator.key) {
        return null;
      }
      for (const categoryKey of Object.keys(indicatorRegistry)) {
        const entry =
          indicatorRegistry[categoryKey].indicators[selectedIndicator.key];
        if (entry) return entry;
      }
      return null;
    }, [selectedIndicator]);

    // Merge registry entry with actual indicator config for editing
    const indicatorEntryForEdit = useMemo(() => {
      if (!registryEntry || !selectedIndicator) {
        return registryEntry;
      }
      return {
        ...registryEntry,
        id: selectedIndicator.id,
        config: selectedIndicator.config || {},
        appearanceConfig: selectedIndicator.appearanceConfig || {},
      };
    }, [registryEntry, selectedIndicator]);

    useImperativeHandle(
      ref,
      () => ({
        show: (moreProps, event, indicator) => {
          setSelectedIndicator(indicator);
          if (ctxRef.current) {
            const nativeEvent = event?.nativeEvent || event;
            const targetDoc = nativeEvent?.target?.ownerDocument ?? document;
            nativeEvent?.preventDefault?.();
            setShowRequest({
              showEvent: nativeEvent,
              body: targetDoc.body,
              seq: ++showSeqRef.current,
            });
          }
        },
        edit: (indicator) => {
          if (!indicator) return;
          setSelectedIndicator(indicator);
          setEditHostBody(getChartHostBody(chartId));
          setEditVisible(true);
        },
        remove: (indicator) => {
          if (!indicator) return;
          if (onRemove && chartId) {
            onRemove(chartId, indicator);
          }
          setSelectedIndicator(null);
        },
      }),
      [chartId, onRemove],
    );

    const handleSave = useCallback(
      (options, appearanceOptions) => {
        if (selectedIndicator && onEditSave && chartId) {
          onEditSave(chartId, selectedIndicator, options, appearanceOptions);
        }
        setEditVisible(false);
      },
      [chartId, onEditSave, selectedIndicator],
    );

    const menuItems = useMemo(
      () => [
        {
          label: "Editar",
          icon: "pi pi-pencil",
          command: () => {
            if (selectedIndicator) {
              setEditHostBody(getChartHostBody(chartId));
              setEditVisible(true);
            }
          },
        },
        {
          label: "Excluir",
          icon: "pi pi-trash",
          command: () => {
            if (selectedIndicator && onRemove && chartId) {
              onRemove(chartId, selectedIndicator);
            }
            setSelectedIndicator(null);
          },
        },
      ],
      [chartId, onRemove, selectedIndicator],
    );

    return (
      <>
        <ContextMenu
          model={menuItems}
          ref={ctxRef}
          appendTo={showRequest?.body ?? document.body}
          baseZIndex={100000}
          onShow={() =>
            requestAnimationFrame(() =>
              clampMenuToViewport(ctxRef.current?.getElement?.()),
            )
          }
        />
        <Suspense fallback={<div></div>}>
          <IndicatorEditModal
            indicatorEntry={indicatorEntryForEdit}
            hostBody={editHostBody}
            visible={editVisible}
            onClose={() => setEditVisible(false)}
            onSave={handleSave}
            chartIndicators={chartIndicators}
            currentPanel={null}
          />
        </Suspense>
      </>
    );
  },
);

IndicatorContextMenu.displayName = "IndicatorContextMenu";

IndicatorContextMenu.propTypes = {
  chartId: PropTypes.string.isRequired,
  onEditSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default React.memo(IndicatorContextMenu);
